import React from 'react'
import { Flame } from 'lucide-react'
import { useEffect, useState } from 'react'
import { dashboardAPI } from '@renderer/api/dashboard'

const RADIUS = 68
const STROKE = 10
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export const LearningProgress: React.FC = () => {
  const [dayStreak, setDayStreak] = useState(0)
  const [progress, setProgress] = useState(0)
  const [completedModules, setCompletedModules] = useState(0)
  const [totalModules, setTotalModules] = useState(0)

  const dashOffset = CIRCUMFERENCE * (1 - progress / 100)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [streakData, progressData] = await Promise.all([
          dashboardAPI.getDayStreak(),
          dashboardAPI.getLabProgressSummary()
        ])
        setDayStreak(streakData.day_streak)
        setProgress(progressData.percentage)
        setCompletedModules(progressData.completed_modules)
        setTotalModules(progressData.total_modules)
      } catch {
        // ignore
      }
    }
    fetchData()
  }, [])

  return (
    <div className="flex flex-col h-full gap-5">
      {/* Header */}
      <div className="grid grid-cols-[1fr_auto] items-center">
        <h1 className="text-2xl leading-[28px] font-bold text-[#181820]">Learning Progress</h1>
        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E0E0E6] shadow-[0_0_12px_rgba(249,115,22,0.3)]">
          <Flame className="w-3 h-3 text-[#F97316]" />
          <span className="text-xs font-semibold text-[#181820]">{dayStreak} Day Streak</span>
        </div>
      </div>

      {/* Circular Progress centered */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative w-[160px] h-[160px] flex items-center justify-center">
          <svg width="160" height="160" className="-rotate-90">
            <circle cx="80" cy="80" r={RADIUS} fill="none" stroke="#E0E0E6" strokeWidth={STROKE} />
            <circle
              cx="80"
              cy="80"
              r={RADIUS}
              fill="none"
              stroke="#7a6020"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          <div className="absolute grid justify-items-center">
            <span className="text-3xl font-bold text-[#181820]">{progress}%</span>
            <span className="text-xs font-semibold text-[#5A5A6D] tracking-widest">DONE</span>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5 p-4 bg-[#f5ede0] rounded-2xl border border-[#e0d4be] shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
          <span className="text-2xl font-bold text-[#181820]">
            {completedModules}
            <span className="text-base font-medium text-[#5A5A6D]"> / {totalModules}</span>
          </span>
          <span className="text-xs font-semibold text-[#5A5A6D] tracking-widest">MODULES</span>
        </div>
        <div className="flex flex-col gap-1.5 p-4 bg-[#f5ede0] rounded-2xl border border-[#e0d4be] shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-1.5">
            <Flame className="w-5 h-5 text-[#F97316]" />
            <span className="text-2xl font-bold text-[#181820]">{dayStreak}</span>
          </div>
          <span className="text-xs font-semibold text-[#5A5A6D] tracking-widest">DAY STREAK</span>
        </div>
      </div>
    </div>
  )
}